import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../App';
import { Check, ChevronsUpDown, Plus, Loader2 } from 'lucide-react';
import { isIOSNative, openExternal } from '../../utils/nativeBridge';
import { requestMobileAuthCode } from '../../utils/nativeHandoff';
import { ExternalLink } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '../ui/dropdown-menu';

const ROLE_LABELS = { owner: 'Owner' };
const roleLabel = (role) =>
  ROLE_LABELS[role] || (role ? role.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : 'Member');

// Company switcher for multi-entity users. Same component serves desktop
// sidebar, mobile menu and PWA (one SPA everywhere).
export const BusinessSwitcher = ({ business, entityInfo, variant = 'sidebar' }) => {
  const navigate = useNavigate();
  const [memberships, setMemberships] = useState(null);
  const [switching, setSwitching] = useState(null);

  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/business/memberships`)
      .then((r) => { if (mounted) setMemberships(r.data); })
      .catch(() => { if (mounted) setMemberships({ businesses: [] }); });
    return () => { mounted = false; };
  }, []);

  const businesses = memberships?.businesses || [];
  const hasMultiple = businesses.length > 1;

  const handleSwitch = async (biz) => {
    if (biz.is_active || switching) return;
    setSwitching(biz.id);
    try {
      const res = await axios.post(`${API}/business/switch`, { business_id: biz.id });
      localStorage.setItem('akountfly_business', JSON.stringify(res.data));
      toast.success(`Switched to ${biz.business_name}`);
      // Full reload so every per-business cache/context rebuilds cleanly.
      window.location.href = '/dashboard';
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not switch business');
      setSwitching(null);
    }
  };

  // iOS App Store build: onboarding/plan selection must happen on the web, so hand the
  // signed-in user to Safari with a one-time code (same mechanism as the signup handoff).
  const [handingOff, setHandingOff] = useState(false);
  const addBusiness = async () => {
    if (!isIOSNative()) { navigate('/onboarding?mode=add'); return; }
    setHandingOff(true);
    const res = await requestMobileAuthCode();
    setHandingOff(false);
    if (!res?.code) { toast.error('Could not open the browser sign-in. Please try again.'); return; }
    const next = encodeURIComponent('/onboarding?mode=add&from=app');
    // src=email keeps the Universal Link in Safari instead of bouncing back into the app.
    openExternal(`${window.location.origin}/auth/callback?code=${encodeURIComponent(res.code)}&next=${next}&src=email`);
  };

  const trigger = (
    <button
      type="button"
      className={`w-full flex items-center gap-2 rounded-lg hover:bg-muted transition-colors text-left ${
        variant === 'mobile' ? 'px-4 py-3' : 'p-1 -m-1'
      }`}
      data-testid="business-switcher-trigger"
    >
      {business?.logo_url ? (
        <img src={business.logo_url} alt={entityInfo.name} className="w-9 h-9 rounded-lg object-cover" />
      ) : (
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ background: `linear-gradient(135deg, ${entityInfo.gradientFrom}, ${entityInfo.gradientTo})` }}
        >
          <span className="text-white font-bold text-sm">{entityInfo.initial}</span>
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="font-medium text-foreground truncate text-sm">{entityInfo.name}</p>
        <p className="text-xs text-muted-foreground truncate">
          {hasMultiple ? `${businesses.length} businesses` : entityInfo.subtitle}
        </p>
      </div>
      <ChevronsUpDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />
    </button>
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64" data-testid="business-switcher-menu">
        <DropdownMenuLabel className="text-xs text-muted-foreground">
          {hasMultiple ? 'Switch business' : 'Your business'}
        </DropdownMenuLabel>
        {businesses.map((biz) => (
          <DropdownMenuItem
            key={biz.id}
            onClick={() => handleSwitch(biz)}
            className="gap-2 cursor-pointer"
            data-testid={`business-switcher-item-${biz.id}`}
          >
            <div
              className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #283F8B, #108981)' }}
            >
              <span className="text-white font-bold text-[11px]">{biz.business_name?.charAt(0) || 'B'}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{biz.business_name}</p>
              <p className="text-[10px] text-muted-foreground">{roleLabel(biz.role)} · {biz.currency}</p>
            </div>
            {switching === biz.id ? (
              <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
            ) : biz.is_active ? (
              <Check className="w-4 h-4 text-[#108981]" data-testid="business-switcher-active-check" />
            ) : null}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={addBusiness}
          disabled={handingOff}
          className="gap-2 cursor-pointer text-[#283F8B] focus:text-[#283F8B]"
          data-testid="business-switcher-add-new"
        >
          <div className="w-7 h-7 rounded-md border border-dashed border-[#283F8B]/40 flex items-center justify-center flex-shrink-0">
            {handingOff ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          </div>
          <span className="text-sm font-medium flex-1">Add new business</span>
          {isIOSNative() && <ExternalLink className="w-3.5 h-3.5 text-muted-foreground" title="Opens in Safari" />}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default BusinessSwitcher;
